import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

interface User {
  id: string;
  email: string;
  name: string | null;
  plan: string;
  hasPin?: boolean;
  pinLockMinutes?: number;
}

interface AuthState {
  user: User | null;
  isLoaded: boolean;
  isSignedIn: boolean;
  isLocked: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string, totpCode?: string) => Promise<{ requires2FA?: boolean }>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => void;
  getToken: () => string | null;
  unlockPin: () => void;
  trustDevice: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const USER_KEY = "potop_user";
const LAST_ACTIVITY_KEY = "potop_last_activity";
const TRUSTED_DEVICE_KEY = "potop_trusted_device";
const PIN_UNLOCKED_KEY = "potop_pin_unlocked";

function saveUserLocal(user: User) {
  try { localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch {}
}

function loadUserLocal(): User | null {
  try {
    const json = localStorage.getItem(USER_KEY);
    if (!json) return null;
    return JSON.parse(json) as User;
  } catch { return null; }
}

function clearLocal() {
  try {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem("potop_token");
    localStorage.removeItem("potop_refresh_token");
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    localStorage.removeItem(PIN_UNLOCKED_KEY);
  } catch {}
}

function isDeviceTrusted(): boolean {
  try {
    const val = localStorage.getItem(TRUSTED_DEVICE_KEY);
    if (!val) return false;
    const expires = Number(val);
    if (Date.now() < expires) return true;
    localStorage.removeItem(TRUSTED_DEVICE_KEY);
    return false;
  } catch { return false; }
}

function shouldLock(user: User | null): boolean {
  if (!user?.hasPin) return false;
  if (isDeviceTrusted()) return false;

  const unlocked = localStorage.getItem(PIN_UNLOCKED_KEY);
  if (unlocked === "true") {
    const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || "0");
    const lockMinutes = user.pinLockMinutes || 5;
    const elapsed = (Date.now() - lastActivity) / 60000;
    if (elapsed < lockMinutes) return false;
  }
  return true;
}

function updateActivity() {
  try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch {}
}

export function getAuthToken(): string | null {
  return null;
}

export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, { ...options, credentials: "include" });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoaded: false,
    isSignedIn: false,
    isLocked: false,
  });

  useEffect(() => {
    let cancelled = false;

    const cachedUser = loadUserLocal();
    if (cachedUser) {
      const locked = shouldLock(cachedUser);
      setState({ user: cachedUser, isLoaded: true, isSignedIn: true, isLocked: locked });
    }

    fetch("/api/auth/me", { credentials: "include" })
      .then(async (r) => {
        if (cancelled) return;
        if (r.ok) {
          const user = await r.json();
          saveUserLocal(user);
          const locked = shouldLock(user);
          setState({ user, isLoaded: true, isSignedIn: true, isLocked: locked });
        } else {
          clearLocal();
          setState({ user: null, isLoaded: true, isSignedIn: false, isLocked: false });
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (!cachedUser) {
          setState({ user: null, isLoaded: true, isSignedIn: false, isLocked: false });
        }
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!state.isSignedIn || state.isLocked) return;
    updateActivity();
    const onActivity = () => updateActivity();
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    const checkInterval = setInterval(() => {
      if (state.user?.hasPin && !isDeviceTrusted()) {
        const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || "0");
        const lockMinutes = state.user.pinLockMinutes || 5;
        const elapsed = (Date.now() - lastActivity) / 60000;
        if (elapsed >= lockMinutes) {
          localStorage.removeItem(PIN_UNLOCKED_KEY);
          setState(prev => ({ ...prev, isLocked: true }));
        }
      }
    }, 30000);

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      clearInterval(checkInterval);
    };
  }, [state.isSignedIn, state.isLocked, state.user]);

  const login = useCallback(async (email: string, password: string, totpCode?: string): Promise<{ requires2FA?: boolean }> => {
    const body: any = { email, password };
    if (totpCode) body.totpCode = totpCode;

    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Ошибка входа");

    if (data.requires2FA) {
      return { requires2FA: true };
    }

    saveUserLocal(data.user);
    updateActivity();
    localStorage.setItem(PIN_UNLOCKED_KEY, "true");
    setState({ user: data.user, isLoaded: true, isSignedIn: true, isLocked: false });
    return {};
  }, []);

  const register = useCallback(async (email: string, password: string, name?: string) => {
    const res = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password, name }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Ошибка регистрации");

    saveUserLocal(data.user);
    updateActivity();
    localStorage.setItem(PIN_UNLOCKED_KEY, "true");
    setState({ user: data.user, isLoaded: true, isSignedIn: true, isLocked: false });
  }, []);

  const logout = useCallback(async () => {
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "include" }); } catch {}
    clearLocal();
    localStorage.removeItem(TRUSTED_DEVICE_KEY);
    setState({ user: null, isLoaded: true, isSignedIn: false, isLocked: false });
  }, []);

  const unlockPin = useCallback(() => {
    updateActivity();
    localStorage.setItem(PIN_UNLOCKED_KEY, "true");
    setState(prev => ({ ...prev, isLocked: false }));
  }, []);

  const trustDevice = useCallback(() => {
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    localStorage.setItem(TRUSTED_DEVICE_KEY, String(Date.now() + thirtyDays));
    updateActivity();
    localStorage.setItem(PIN_UNLOCKED_KEY, "true");
    setState(prev => ({ ...prev, isLocked: false }));
  }, []);

  const getToken = useCallback(() => null, []);

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, getToken, unlockPin, trustDevice }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function useUser() {
  const { user, isSignedIn } = useAuth();
  return { user, isSignedIn };
}
